import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Pause, Pencil, Play, Plus, Zap } from 'lucide-react';
import {
  CATCH_UP,
  ROUTINE_DELIVERIES,
  ROUTINE_TARGETS,
  ROUTINE_TRIGGERS,
  createRoutine,
  listAgents,
  listRoutineEvents,
  listRoutines,
  pauseRoutine,
  resumeRoutine,
  runRoutine,
  updateRoutine,
} from '@mkcore/agents/schema';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  NativeSelect,
  PageHeader,
  Skeleton,
  TBody,
  THead,
  Table,
  Td,
  Textarea,
  Th,
  Tr,
} from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { RunStatusBadge } from '@/features/agents/ai';
import { routineWhen, type Routine } from '@/features/agents/routines';

export const Route = createFileRoute('/_app/agents/routines')({ component: Routines });

const key = ['agent', 'routine'];
/** An instant as the value of a datetime-local field, in the browser's zone. */
const localInput = (iso: string) => {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};

/**
 * Routines (CO-1250): an instruction for an agent with a trigger. The register with its next moment and last run,
 * pausing, resuming and running now; the runs themselves are under /agents/runs.
 */
function Routines() {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const canUpdate = useCan('agents:update');
  const q = useQuery({ queryKey: key, queryFn: () => call(listRoutines, {}) });
  const invalidate = [key, ['agent', 'run']];
  const pause = useActionMutation(pauseRoutine, { invalidate });
  const resume = useActionMutation(resumeRoutine, { invalidate });
  const run = useActionMutation(runRoutine, { invalidate, success: t('agents.routines.started') });
  const [editing, setEditing] = useState<Routine | 'new' | null>(null);

  return (
    <>
      <Link to="/agents" className="mb-3 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg-1">
        <ArrowLeft className="size-4" /> {t('agents.title')}
      </Link>
      <PageHeader
        eyebrow={t('nav.agents')}
        title={t('agents.routines.title')}
        description={t('agents.routines.description')}
        actions={
          canUpdate && (
            <Button onClick={() => setEditing('new')}>
              <Plus /> {t('agents.routines.new')}
            </Button>
          )
        }
      />
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : !q.data ? (
        <Skeleton className="h-48" />
      ) : !q.data.items.length ? (
        <EmptyState title={t('agents.routines.none')} description={t('agents.routines.noneHint')} />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>{t('common.name')}</Th>
              <Th className="hidden md:table-cell">{t('agents.routines.when')}</Th>
              <Th>{t('common.status')}</Th>
              <Th className="hidden md:table-cell">{t('agents.routines.next')}</Th>
              <Th className="hidden lg:table-cell">{t('agents.routines.lastRun')}</Th>
              <Th className="sr-only">{t('common.actions')}</Th>
            </Tr>
          </THead>
          <TBody>
            {q.data.items.map((r) => (
              <Tr key={r.id} aria-label={r.name}>
                <Td>
                  <div className="font-bold">{r.name}</div>
                  <div className="text-xs text-fg-3">{r.agentName ?? t('agents.routines.noAgent')}</div>
                </Td>
                <Td className="hidden md:table-cell text-sm text-fg-2">{routineWhen(t, r)}</Td>
                <Td>
                  <Badge tone={r.status === 'active' ? 'success' : 'warning'}>{t(`agents.routines.status.${r.status}`)}</Badge>
                </Td>
                <Td className="hidden md:table-cell tabular text-fg-2">{r.nextFireAt ? formatDateTime(r.nextFireAt) : '—'}</Td>
                <Td className="hidden lg:table-cell">
                  {r.lastRun ? (
                    <Link to="/agents/runs/$id" params={{ id: r.lastRun.id }} className="inline-flex items-center gap-2 hover:underline">
                      <RunStatusBadge status={r.lastRun.status} />
                      <span className="text-xs text-fg-3">{formatDateTime(r.lastRun.startedAt)}</span>
                    </Link>
                  ) : (
                    '—'
                  )}
                </Td>
                <Td className="text-right">
                  {canUpdate && (
                    <div className="flex flex-wrap justify-end gap-1">
                      <Button size="sm" variant="secondary" loading={run.isPending && run.variables?.input.id === r.id} onClick={() => run.mutate({ input: { id: r.id } })}>
                        <Zap /> {t('agents.routines.runNow')}
                      </Button>
                      {r.status === 'active' ? (
                        <Button size="sm" variant="ghost" onClick={() => pause.mutate({ input: { id: r.id } })}>
                          <Pause /> {t('agents.pause')}
                        </Button>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => resume.mutate({ input: { id: r.id } })}>
                          <Play /> {t('agents.resume')}
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" aria-label={t('agents.routines.editName', { name: r.name })} onClick={() => setEditing(r)}>
                        <Pencil />
                      </Button>
                    </div>
                  )}
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
      {editing && <RoutineDialog routine={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function RoutineDialog({ routine, onClose }: { routine: Routine | null; onClose: () => void }) {
  const { t } = useTranslation();
  const agents = useQuery({ queryKey: ['agent', 'agent'], queryFn: () => call(listAgents, {}) });
  const events = useQuery({ queryKey: ['agent', 'routineEvent'], queryFn: () => call(listRoutineEvents, {}) });
  const [name, setName] = useState(routine?.name ?? '');
  const [instruction, setInstruction] = useState(routine?.instruction ?? '');
  const [agentId, setAgentId] = useState(routine?.agentId ?? '');
  const [domain, setDomain] = useState(routine?.domain ?? '');
  const [trigger, setTrigger] = useState<(typeof ROUTINE_TRIGGERS)[number]>(routine?.trigger ?? 'cron');
  const [cron, setCron] = useState(routine?.cron ?? '0 7 * * 1-5');
  const [timezone, setTimezone] = useState(routine?.timezone ?? 'Europe/Amsterdam');
  const [interval, setIntervalMinutes] = useState(String(routine?.intervalMinutes ?? 60));
  const [runAt, setRunAt] = useState(routine?.runAt ? localInput(routine.runAt) : '');
  const [eventType, setEventType] = useState(routine?.eventType ?? '');
  const [filter, setFilter] = useState(
    Object.entries(routine?.eventFilter ?? {})
      .map(([k, v]) => `${k}=${String(v)}`)
      .join('\n'),
  );
  const [agentEvents, setAgentEvents] = useState(routine?.agentEvents ?? false);
  const [maxRuns, setMaxRuns] = useState(String(routine?.maxRunsPerHour ?? 10));
  const [target, setTarget] = useState<(typeof ROUTINE_TARGETS)[number]>(routine?.target ?? 'notify');
  const [deliverAs, setDeliverAs] = useState<(typeof ROUTINE_DELIVERIES)[number]>(routine?.deliverAs ?? 'result');
  const [chat, setChat] = useState(routine?.chat ?? false);
  const [mailTo, setMailTo] = useState((routine?.mailTo ?? []).join(', '));
  const [catchUp, setCatchUp] = useState<(typeof CATCH_UP)[number]>(routine?.catchUp ?? 'once');
  const [repository, setRepository] = useState(routine?.repository ?? '');
  const [branch, setBranch] = useState(routine?.branch ?? '');
  const [maxTurns, setMaxTurns] = useState(routine?.maxTurns ? String(routine.maxTurns) : '');
  const [timeoutMinutes, setTimeoutMinutes] = useState(routine?.timeoutMinutes ? String(routine.timeoutMinutes) : '');
  const invalidate = [key];
  const create = useActionMutation(createRoutine, { invalidate, success: t('agents.routines.created'), onSuccess: onClose });
  const update = useActionMutation(updateRoutine, { invalidate, success: t('agents.routines.saved'), onSuccess: onClose });
  const eventFilter = Object.fromEntries(
    filter
      .split('\n')
      .map((l) => l.split('='))
      .filter((p) => p.length === 2 && p[0]!.trim())
      .map(([k, v]) => [k!.trim(), v!.trim()]),
  );
  const fields = {
    instruction,
    agentId: agentId || null,
    domain: agentId ? null : domain.trim() || null,
    trigger,
    cron: trigger === 'cron' ? cron.trim() : null,
    timezone,
    intervalMinutes: trigger === 'interval' ? Number(interval) : null,
    runAt: trigger === 'once' && runAt ? new Date(runAt).toISOString() : null,
    eventType: trigger === 'event' ? eventType || null : null,
    eventFilter: trigger === 'event' ? eventFilter : {},
    agentEvents: trigger === 'event' && agentEvents,
    maxRunsPerHour: Number(maxRuns) || 10,
    target,
    deliverAs: target === 'notify' ? deliverAs : 'result',
    chat: target === 'notify' && chat,
    mailTo: target === 'mail_draft' ? mailTo.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean) : [],
    catchUp,
    repository: repository.trim() || null,
    branch: repository.trim() && branch.trim() ? branch.trim() : null,
    maxTurns: Number(maxTurns) > 0 ? Math.round(Number(maxTurns)) : null,
    timeoutMinutes: Number(timeoutMinutes) > 0 ? Math.round(Number(timeoutMinutes)) : null,
  };
  const runner = (agents.data?.items ?? []).find((a) => a.id === agentId);
  const onWorker = runner?.runtime === 'claude_code' || runner?.runtime === 'cli';
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent closeLabel={t('common.close')} className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{routine ? t('agents.routines.edit', { name: routine.name }) : t('agents.routines.new')}</DialogTitle>
        </DialogHeader>
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (routine) update.mutate({ input: { id: routine.id, expectedVersion: routine.version, name: name.trim(), ...fields } });
            else create.mutate({ input: { name: name.trim(), ...fields } });
          }}
        >
          <Field label={t('common.name')}>{(p) => <Input {...p} required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />}</Field>
          <Field label={t('agents.routines.instruction')} hint={t('agents.routines.instructionHint')}>
            {(p) => <Textarea {...p} required rows={5} value={instruction} onChange={(e) => setInstruction(e.target.value)} />}
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('agents.runs.agent')}>
              {(p) => (
                <NativeSelect {...p} value={agentId} onChange={(e) => setAgentId(e.target.value)}>
                  <option value="">{t('agents.routines.defaultAgent')}</option>
                  {(agents.data?.items ?? [])
                    .filter((a) => a.status !== 'terminated')
                    .map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                </NativeSelect>
              )}
            </Field>
            {!agentId && (
              <Field label={t('agents.routines.domain')} hint={t('agents.routines.domainHint')}>
                {(p) => <Input {...p} value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="projects" />}
              </Field>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('agents.routines.trigger')}>
              {(p) => (
                <NativeSelect {...p} value={trigger} onChange={(e) => setTrigger(e.target.value as typeof trigger)}>
                  {ROUTINE_TRIGGERS.map((tr) => (
                    <option key={tr} value={tr}>
                      {t(`agents.routines.triggers.${tr}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            {trigger === 'cron' && (
              <Field label={t('agents.routines.cron')} hint={t('agents.routines.cronHint')}>
                {(p) => <Input {...p} className="font-mono" value={cron} onChange={(e) => setCron(e.target.value)} />}
              </Field>
            )}
            {trigger === 'interval' && (
              <Field label={t('agents.routines.interval')}>{(p) => <Input {...p} type="number" min={5} value={interval} onChange={(e) => setIntervalMinutes(e.target.value)} />}</Field>
            )}
            {trigger === 'once' && <Field label={t('agents.routines.runAt')}>{(p) => <Input {...p} type="datetime-local" value={runAt} onChange={(e) => setRunAt(e.target.value)} />}</Field>}
            {trigger === 'event' && (
              <Field label={t('agents.routines.event')}>
                {(p) => (
                  <NativeSelect {...p} value={eventType} onChange={(e) => setEventType(e.target.value)}>
                    <option value="">—</option>
                    {(events.data?.items ?? []).map((ev) => (
                      <option key={ev.type} value={ev.type}>
                        {ev.type}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
            )}
          </div>
          {trigger === 'cron' && <Field label={t('agents.routines.timezone')}>{(p) => <Input {...p} value={timezone} onChange={(e) => setTimezone(e.target.value)} />}</Field>}
          {trigger === 'event' && (
            <Field label={t('agents.routines.filter')} hint={t('agents.routines.filterHint', { fields: events.data?.items.find((ev) => ev.type === eventType)?.fields.join(', ') ?? '—' })}>
              {(p) => <Textarea {...p} rows={2} className="font-mono text-xs" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="change=created" />}
            </Field>
          )}
          {trigger === 'event' && (
            <div className="grid gap-3 sm:grid-cols-2 sm:items-end">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={agentEvents} onChange={(e) => setAgentEvents(e.target.checked)} /> {t('agents.routines.agentEvents')}
              </label>
              <Field label={t('agents.routines.maxRunsPerHour')} hint={t('agents.routines.maxRunsHint')}>
                {(p) => <Input {...p} type="number" min={1} max={100} value={maxRuns} onChange={(e) => setMaxRuns(e.target.value)} />}
              </Field>
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('agents.routines.target')}>
              {(p) => (
                <NativeSelect {...p} value={target} onChange={(e) => setTarget(e.target.value as typeof target)}>
                  {ROUTINE_TARGETS.map((tg) => (
                    <option key={tg} value={tg}>
                      {t(`agents.routines.targets.${tg}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            {target === 'notify' && (
              <Field label={t('agents.routines.deliverAs')} hint={t('agents.routines.deliverAsHint')}>
                {(p) => (
                  <NativeSelect {...p} value={deliverAs} onChange={(e) => setDeliverAs(e.target.value as typeof deliverAs)}>
                    {ROUTINE_DELIVERIES.map((d) => (
                      <option key={d} value={d}>
                        {t(`agents.routines.deliveries.${d}`)}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
            )}
            {target === 'notify' && (
              <label className="flex items-center gap-2 text-sm sm:self-end">
                <input type="checkbox" checked={chat} onChange={(e) => setChat(e.target.checked)} /> {t('agents.routines.chat')}
              </label>
            )}
            {target === 'mail_draft' && (
              <Field label={t('agents.routines.mailTo')} hint={t('agents.routines.mailToHint')}>
                {(p) => <Input {...p} value={mailTo} onChange={(e) => setMailTo(e.target.value)} placeholder="klant@voorbeeld.nl" />}
              </Field>
            )}
            <Field label={t('agents.routines.catchUp')}>
              {(p) => (
                <NativeSelect {...p} value={catchUp} onChange={(e) => setCatchUp(e.target.value as typeof catchUp)}>
                  {CATCH_UP.map((c) => (
                    <option key={c} value={c}>
                      {t(`agents.routines.catchUps.${c}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
          </div>
          {onWorker && <p className="text-sm text-fg-2">{t('agents.routines.onWorker')}</p>}
          <div className="grid gap-3 sm:grid-cols-4">
            <Field label={t('agents.routines.repository')} hint={onWorker ? t('agents.routines.repositoryHint') : undefined}>
              {(p) => <Input {...p} className="font-mono text-xs" value={repository} onChange={(e) => setRepository(e.target.value)} placeholder="HNC/clippy" />}
            </Field>
            <Field label={t('agents.routines.branch')}>{(p) => <Input {...p} className="font-mono text-xs" value={branch} disabled={!repository.trim()} onChange={(e) => setBranch(e.target.value)} placeholder="develop" />}</Field>
            <Field label={t('agents.routines.maxTurns')}>{(p) => <Input {...p} inputMode="numeric" value={maxTurns} onChange={(e) => setMaxTurns(e.target.value.replace(/\D/g, ''))} placeholder="60" />}</Field>
            <Field label={t('agents.routines.timeoutMinutes')}>{(p) => <Input {...p} inputMode="numeric" value={timeoutMinutes} onChange={(e) => setTimeoutMinutes(e.target.value.replace(/\D/g, ''))} placeholder="30" />}</Field>
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={create.isPending || update.isPending} disabled={!name.trim() || !instruction.trim()}>
              {routine ? t('common.save') : t('common.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
